/**
 *  1:1 문의 목록
 *
 *  @author  Bruce.Cheong (2026-05-06)
 *
 */

/** External Imports */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/** Internal import */
import CSS                  from    "./QnaList.module.css";
import * as commonTools     from    "@/Common/ts/commonTools";
import { getAccountData }   from    "@/Common/ts/commonTools";   // 접속한 사용자 정보

import CardContainer        from    "@/Component/Elements/CardContainer/CardContainer";
import Button               from    "@/Component/Elements/Button/Normal/Button";
import MainTitle            from    "@/Component/Elements/MainTitle/MainTitle";
import SearchIcon           from    "@/Component/Elements/Icon/SearchIcon/SearchIcon";
import Input                from    "@/Component/Elements/Input/Input";

import Answer               from    "./QnaList/Answer";
import List                 from    "./QnaList/List";
import Write                from    "./QnaList/Write";

// fetch.ts import
import fetchBE              from    "@/Common/fetch/fetchBE";

/** 목록 테이블 컬럼 정의 */
type it_ListField = "post_contents" | "answer_status" | "post_insert_dtm";

interface it_ListTitleItem {
    field: it_ListField;
    title: string;
}

/** 목록과 상세 팝업에서 함께 사용하는 게시물 모델 */
interface it_PostItem {
    post_pk: number;
    group_name: string;
    post_title: string;
    post_contents: string;
    post_insert_dtm: string;
    answer_pk: number | null;
    answer_contents: string | null;
    answer_insert_dtm: string | null;
    answer_status: "답변완료" | "답변대기";
    [key: string]: unknown;
}

const LIST_TITLE: it_ListTitleItem[] = [
    { field: "answer_status", title: "상태" },
    { field: "post_contents", title: "질문" },
    { field: "post_insert_dtm", title: "등록일" },
];





function QnaList() {

    const officeId = getAccountData.office_id();

    const navigate = useNavigate();
    const param = useParams<{ postPk?: string }>();

    /** 게시물 fetchData */
    const [fetchPostList, setFetchPostList]   = useState<it_PostItem[]>([]);
    /** 게시물 상세 팝업에 노출할 게시물 내용 */
    const [postContent, setPostContent]       = useState<it_PostItem | null>(null);
    /** 게시물 목록 로딩 여부 */
    const [isPostListLoaded, setIsPostListLoaded] = useState<boolean>(false);
    /** 검색어 */
    const [searchKeyword, setSearchKeyword]   = useState<string>("");

    /** 문의내용 확인을 위한 postPk */
    const detailPostPk = useMemo(() => {
        const postPk = Number(param.postPk);

        if (!param.postPk || Number.isNaN(postPk)) {
            return null;
        }

        return postPk;
    }, [param.postPk]);

    /** 문의글 작성 */
    const isWriteOpen = useMemo(() => {
        /** param의 마지막이 write인지 확인 */
        const pathSegments = window.location.pathname.split("/").filter(Boolean);
        return pathSegments[pathSegments.length - 1] === "write";
    }, [window.location.pathname]);

    /** 게시물 목록 fetch */
    useEffect(() => {
        f_Search();

        return () => {
            setFetchPostList([]);
            setIsPostListLoaded(false);
        }
    }, []);

    /** postPk가 있는 경우 답변 setState */
    useEffect(() => {
        if (detailPostPk === null) {
            setPostContent(null);
            return;
        }

        setPostContent(
            fetchPostList.find((iPost) => iPost.post_pk === detailPostPk) || null
        );
    }, [detailPostPk, fetchPostList]);

    const detailTitle = postContent?.post_title
        || (isPostListLoaded ? "게시물을 찾을 수 없습니다." : "게시물을 불러오는 중입니다.");
    const inquiryBody = postContent?.post_contents
        || (isPostListLoaded ? "선택한 게시물의 내용을 확인할 수 없습니다." : "잠시만 기다려주세요.");
    const answerBody = postContent?.answer_contents
        || (isPostListLoaded ? "아직 등록된 답변이 없습니다." : "잠시만 기다려주세요.");
    const answerInsertDtm = postContent?.answer_insert_dtm
        || (isPostListLoaded ? "" : "답변 등록일을 불러오는 중입니다.");
    const postAuthor = postContent?.group_name || "고객";
    const postStatus = postContent?.answer_status || "답변대기";
    const postInsertDtm = postContent?.post_insert_dtm || "-";

    // 검색
    async function f_Search() {
        const _fetchParam = {
            board_pk: 1,
            group_id: 8, // officeId,
            search_value: "",
        };

        _fetchParam.search_value = searchKeyword.trim();

        const _fetchData = await fetchBE.get.postList(_fetchParam);

        let postList: Record<string, any>[] = [];
        const fetchDataRoot = _fetchData.data as null | Record<string, any> | Record<string, any>[];

        if (fetchDataRoot && !Array.isArray(fetchDataRoot) && Array.isArray(fetchDataRoot.data)) {
            postList = fetchDataRoot.data;
        } else if (Array.isArray(fetchDataRoot)) {
            postList = fetchDataRoot;
        }

        setFetchPostList(postList.map((iRow) => {
            const _answerPk = Number(iRow.answer_pk);
            const _answerContents = `${iRow.answer_contents || ""}`.trim();
            const _hasAnswer = (!Number.isNaN(_answerPk) && _answerPk > 0) || _answerContents.length > 0;

            return {
                ...iRow,
                post_pk: Number(iRow.post_pk),
                group_name: `${iRow.group_name || "고객"}`,
                post_title: `${iRow.post_title || ""}`,
                post_contents: `${iRow.post_contents || ""}`,
                post_insert_dtm: commonTools.formatDateTime(`${iRow.post_insert_dtm || ""}`),
                answer_pk: Number.isNaN(_answerPk) ? null : _answerPk,
                answer_contents: _answerContents || null,
                answer_insert_dtm: iRow.answer_insert_dtm
                    ? commonTools.formatDateTime(`${iRow.answer_insert_dtm}`)
                    : null,
                answer_status: _hasAnswer ? "답변완료" : "답변대기",
            };
        }));
        setIsPostListLoaded(true);
    }



    // 검색 Input Change (Bruce.Cheong 2026-05-12)
    function hdlChangeSearchInput(iEvt: React.ChangeEvent<HTMLInputElement>) {
        setSearchKeyword(iEvt.target.value);
    }

    // 검색 Input KeyDown (Bruce.Cheong 2026-05-12)
    function hdlKeyDownSearchInput(iEvt: React.KeyboardEvent<HTMLInputElement>) {
        // enter 키 입력 시 검색 실행
        if ( iEvt.key == "Enter" ) { f_Search(); }
    }

    // 검색 버튼 클릭 (Bruce.Cheong 2026-05-12)
    function hdlClickSearch() {
        f_Search();
    }





    return (
        <div className={CSS.QnaList}>
            <MainTitle title="1:1 문의" />

            <CardContainer noStyle>
                <div className={CSS.HelpCard}>
                    <div className={CSS.HelpMain}>
                        <h3 className={CSS.HelpTitle}>
                            무엇을 도와드릴까요?
                        </h3>
                        <p className={CSS.HelpDescription}>
                            스튜디오 이용과 관련된 궁금한 점을 남겨주세요.
                        </p>
                    </div>
                </div>
            </CardContainer>

            <div className={CSS.Toolbar}>
                <label className={CSS.SearchField}>
                    <span className={CSS.SearchIconWrap}>
                        <SearchIcon />
                    </span>
                    <Input
                        inputClassName={CSS.SearchInput}
                        type="text"
                        placeholder="검색어를 입력하세요"
                        aria-label="검색어를 입력하세요"
                        onChange={hdlChangeSearchInput}
                        onKeyDown={hdlKeyDownSearchInput}
                    />
                    <Button
                        preset={"background_white"}
                        class_name_list={[CSS.ToolbarButton, CSS.SearchButton]}
                        onClick={hdlClickSearch}
                    >
                        검색
                    </Button>
                </label>

                <Button
                    preset={"background_white"}
                    class_name_list={[CSS.ToolbarButton, CSS.WriteActionButton]}
                    onClick={() => {
                        navigate("/qnaList/write");
                    }}
                >
                    문의글 작성
                </Button>
            </div>

            <List
                listTitle={LIST_TITLE}
                listData={fetchPostList}
                rowClickHandler={(iPost) => {
                    navigate(`/qnaList/${iPost.post_pk}`);
                }}
            />

            <Write
                isOpen={isWriteOpen}
                onSuccess={async () => {
                    await f_Search();
                }}
                onClose={() => {
                    // setIsWriteOpen(false);
                    navigate("/qnaList");
                }}
            />

            <Answer
                isOpen={detailPostPk !== null}
                onClose={() => {
                    navigate("/qnaList");
                }}
                detailTitle={detailTitle}
                postAuthor={postAuthor}
                status={postStatus}
                postDate={postInsertDtm}
                inquiryBody={inquiryBody}
                answerBody={answerBody}
                answerInsertDtm={answerInsertDtm}
            />
        </div>
    );
}

export default QnaList;
